import React, {
    useContext,
    useCallback,
    useState,
    useEffect
} from 'react';
import _ from 'lodash';
import { BreakpointTrackerContext } from '@jutro/layout';

import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { ClausesUtil } from '@xengage/gw-policycommon-util-js';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
// import { CodegenContext } from '../../components/CodegenContext';
import presentationMetadata from './CoveragesPage.metadata.json5';
import './CoveragesPage.messages';

function CoveragesPage(props) {
    const { authHeader } = useAuthentication();
    const [loadingClause, updateLoadingClause] = useState();
    // currentStep
    const {
        wizardData: submissionVM, updateWizardData, index,  clausesBasePath, vehicleVM
    } = props;
    const { LoadSaveService } = useDependencies('LoadSaveService');
 	const { EndorsementService } = useDependencies('EndorsementService');
    const metadata = presentationMetadata["RecVehProduct.default.coverages"];
    const viewModelService = useContext(ViewModelServiceContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const {
        onValidate,
        initialValidation,
        isComponentValid,
        disregardFieldValidation,
        registerComponentValidation,
        registerInitialComponentValidation
    } = useValidation('CoveragesPage');
    // const clausesBasePath = `lobData.${lineName}.lineCoverages`;

    const updateClauses = useCallback(
        async () => {
            const { quoteID, jobID, sessionUUID } = submissionVM.value;

            const lobName = ClausesUtil.getLobNameFromPath(clausesBasePath);
            const removeLastIndexPath = clausesBasePath.replace('.clausesGroups', '');
            
            const clausesToUpdate = ['coverages', 'endorsementCoverages'].reduce((payloadToSend, clauseType) => {
                let clausePathValue = _.get(submissionVM, `${clausesBasePath}.${clauseType}.value`);

                if (!clausePathValue) {
                    clausePathValue = _.get(submissionVM.value, `${clausesBasePath}.${clauseType}`);
                }

                const clauses = clausePathValue || [];

                return _.merge(
                    payloadToSend,
                    ClausesUtil.structureClausesForServer(clauses, lobName, clauseType)
                );
            }, {});

            // As the clause is set to edited
            // ClausesUtil.filterUnchangedClauses will pick it up in structureClausesForServer
            clausesToUpdate.recVehLine.coverableFixedId = _.get(submissionVM.value, `${removeLastIndexPath}.fixedID`);

            const {jobType} = submissionVM.value.baseData;
            const jobService = jobType === 'PolicyChange' ? EndorsementService : LoadSaveService;
            
            const response = await jobService
                .updateCoverages(quoteID || jobID, sessionUUID, clausesToUpdate, authHeader);
            const updatedClauses = _.get(response, lobName);
            const newSubmissionVM = viewModelService.clone(submissionVM);

            _.set(submissionVM.value, clausesBasePath, updatedClauses.vehicleClausesGroups[0].clausesGroups);            

            const newClauseIDsToRemove = ['coverages', 'endorsementCoverages'].reduce((currentInvalidFields, clauseType) => {
                const clauseIDsToRemove = (
                    _.get(submissionVM, `${clausesBasePath}.${clauseType}.value`)
                    && _.get(newSubmissionVM, `${clausesBasePath}.${clauseType}.value`))
                    ? ClausesUtil.getRemovedClausesID(submissionVM, newSubmissionVM, `${clausesBasePath}.${clauseType}`) : [];

                return [
                    ...currentInvalidFields,
                    ...clauseIDsToRemove
                ];
            }, []);

            disregardFieldValidation(newClauseIDsToRemove);
            updateWizardData(submissionVM);
            updateLoadingClause(false);
            

            return submissionVM;
        },
        [submissionVM, clausesBasePath, EndorsementService, LoadSaveService, authHeader, viewModelService, disregardFieldValidation, updateWizardData]
    );

    const changeSubmission = useCallback(
        (value, changedPath) => {
           // value = LocalDateUtil.changeDateFormatForDirectDateValue(value, changedPath);
            updateWizardData(
                ClausesUtil.setClauseValue(submissionVM, value, changedPath)
            );
        },
        [submissionVM, updateWizardData]
    );


    const syncCoverages = useCallback(async (value, changedPath) => {
        const basePath = ClausesUtil.getObjectPathFromChangedPath(changedPath);
        let baseObject = _.get(submissionVM, basePath);

        if (!baseObject) {
            const escapedClausesBasePath = clausesBasePath.replace(/\[/g, '\\[').replace(/\]/g, '\\]');
            const clausesPath = changedPath.match(`${escapedClausesBasePath}.(coverages|endorsementCoverages)`)[0];
            const baseObjectArray = _.get(submissionVM.value, clausesPath);
            const selectedBaseObject = baseObjectArray.filter((data)=>data.updated === true);
            
            // eslint-disable-next-line prefer-destructuring
            baseObject = selectedBaseObject[0];
        }

        // eslint-disable-next-line no-secrets/no-secrets
        // updateLoadingClause(baseObject?.coveragePublicID || baseObject?.publicID);
        updateLoadingClause(true)

        const newSubmissionData = await updateClauses();

        updateWizardData(newSubmissionData);

    }, [clausesBasePath, updateClauses, submissionVM, updateWizardData]);


    const changeSubmissionAndSync = useCallback(
        (value, changedPath) => {
            changeSubmission(value, changedPath);
            syncCoverages(value, changedPath);
        },
        [changeSubmission, syncCoverages]
    );

    const onNext = useCallback(
        updateClauses,
        [updateClauses]
    );

    const resolvers = {
        resolveCallbackMap: {
            onChangeClause: changeSubmission,
            onSyncCoverages: syncCoverages,
            onChangeSubmissionAndSync: changeSubmissionAndSync,
            onValidate,
        },
    };

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        coverages: {
            loadingClause,
            path: `${clausesBasePath}.coverages`
        },
        endorsementcoverages: {
            loadingClause,
            path: `${clausesBasePath}.endorsementCoverages`
        }
    };

    const shouldPageSkip = useCallback(() => {
        const coverages = _.get(submissionVM.value, clausesBasePath);
        const jobType = _.get(submissionVM.value, 'baseData.jobType');

        if (jobType === 'Submission') {
            return true;
        }

        const status = _.get(submissionVM.value, 'status');

        return status === 'Quoted' || (coverages && !_.find(coverages, { isValid: false }));
    }, [clausesBasePath, submissionVM.value]);

    const isPageLoading = useCallback(() => _.isUndefined(loadingClause), [loadingClause]);

    useEffect(() => {
        registerComponentValidation(isPageLoading);
        registerInitialComponentValidation(shouldPageSkip);
    }, [
        isPageLoading,
        registerComponentValidation,
        registerInitialComponentValidation,
        shouldPageSkip
    ]);

    return (
        /* <WizardPage
            onNext={onNext}
            skipWhen={() => initialValidation}
            disableNext={!isComponentValid}
        > */
            <ViewModelForm
                uiProps={metadata.content}
                model={submissionVM.value}
                overrideProps={overrideProps}
                onValidationChange={onValidate}
                onModelChange={updateWizardData}
                callbackMap={resolvers.resolveCallbackMap}
            />
        //</WizardPage>
    );
}

CoveragesPage.propTypes = {
    ...wizardProps
};

export default CoveragesPage;